import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '@/services/auth';
import { getMarketingSiteOrigin } from '@/utils/hostOrigins';
import ArrangellyBrand from '@/components/brand/ArrangellyBrand';

const FAILED_LOGIN_ATTEMPTS_KEY = 'arrangelly_dashboard_failed_login_attempts';
const MAX_FAILED_ATTEMPTS = 3;

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const websiteUrl = getMarketingSiteOrigin();

  const getFailedAttempts = () => {
    const raw = sessionStorage.getItem(FAILED_LOGIN_ATTEMPTS_KEY);
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  const setFailedAttempts = (value: number) => {
    sessionStorage.setItem(FAILED_LOGIN_ATTEMPTS_KEY, String(value));
  };

  // If the user is already authenticated (cookie present), skip the login UI
  // and go straight to the dashboard root.
  useEffect(() => {
    const existingUser = authService.getUser();
    if (existingUser) {
      console.log('[Dashboard LoginPage] existing user cookie found, redirecting to /', existingUser);
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');
    setIsLoading(true);

    try {
      console.log('[Dashboard LoginPage] handleSubmit: calling authService.login...', { email });
      const response = await authService.login({ email, password });
      console.log('[Dashboard LoginPage] handleSubmit: login response', response);

      // Small delay to ensure cookies are set by the API
      await new Promise((resolve) => setTimeout(resolve, 100));
      setFailedAttempts(0);
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Dashboard login error:', err);
      const nextFailedAttempts = getFailedAttempts() + 1;
      setFailedAttempts(nextFailedAttempts);

      if (nextFailedAttempts >= MAX_FAILED_ATTEMPTS) {
        setError('Incorrect email or password. You have reached 3 failed attempts. Redirecting...');
        setTimeout(() => {
          window.location.href = websiteUrl;
        }, 700);
      } else {
        const remaining = MAX_FAILED_ATTEMPTS - nextFailedAttempts;
        setError(
          `Incorrect email or password. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - Arrangelly Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-violet-50/30 to-slate-100 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md bg-white/95 border border-slate-200 shadow-lg shadow-slate-200/50 rounded-2xl p-8 backdrop-blur-md">
          <div className="flex justify-center mb-8">
            <ArrangellyBrand size="lg" layout="horizontal" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-6 text-center">Log in to your studio</h1>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-600 mb-1" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full mt-2 bg-accent hover:bg-accent/90 text-accent-foreground disabled:opacity-50"
            >
              {isLoading ? 'Signing in...' : 'Log in'}
            </Button>
            <p className="mt-3 text-center">
              <Link to="/forgot-password" className="text-sm text-violet-600 hover:text-violet-700 underline">
                Forgot password?
              </Link>
            </p>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginPage;

